import { arBrfKund } from '../../../utils/nameUtils';

/**
 * Offertens villkor — en enda källa.
 *
 * Texten fanns tidigare i två kopior, ord för ord: generateOffertPdf.js för
 * PDF:en och TermsSection.js för skärmen. De hade hunnit glida isär på tre
 * ställen, så kunden läste olika formuleringar beroende på var hen tittade.
 * PDF:ens lydelse var den riktiga i samtliga tre och är den som gäller här:
 *
 *   "föra entreprenören med skisser"            -> "förse entreprenören med skisser"
 *   "material som eller i efterhand debiteras"  -> "material debiteras"
 *   "moms/h. plus transportavgifter"            -> "moms/h plus transportavgifter"
 *
 * Lägg aldrig tillbaka villkorstext i vyerna. Ska en formulering ändras, ändra
 * den här — då följer både skärm och PDF med.
 */

const TIMPRIS_MIN = 650;
const TIMPRIS_MAX = 890;
const MATERIALPRIS = 570;
const INNEHALLNING_STANDARD = 10;
const GILTIGHET_DAGAR = 60;

/**
 * Formaterar bara riktiga ISO-datum. Fritext som "augusti", "v.32" eller "2/11"
 * lämnas orörd — new Date() feltolkar dem (2/11 blir amerikanskt MM/DD och
 * hamnar på 11 februari 2001).
 */
export const formateraVillkorsdatum = (varde) => {
  if (!varde) return '';
  const str = String(varde);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
    }
  }
  return str;
};

/**
 * Bygger villkorslistan för en offert.
 *
 * @param {object} offert
 * @param {object} procurement
 * @param {object} [customer] - Beställaren. Utelämnas den läses procurement.customerId.
 * @returns {Array<{ text: string, highlight?: boolean }>} i visningsordning
 */
export const byggOffertVillkor = ({ offert, procurement, customer } = {}) => {
  const kund = customer ?? procurement?.customerId;
  const arBrf = arBrfKund(kund);

  // ABS18 gäller tillbyggnader. Villkoret innehöll tidigare även
  // projectType === 'Nyproduktion', men Nyproduktion ligger i propertyType och
  // finns inte i projectType-enumet, så den halvan kunde aldrig bli sann.
  const arTillbyggnad = procurement?.projectInfo?.projectType === 'Tillbyggnad';

  const harHembesok = !procurement?.homeVisitSkipped
    && !!procurement?.homeVisitWorkflow?.booking?.meetingAt;

  const priser = offert?.puThreadId?.recipient?.builderId?.pricing || {};
  const timprisMin = priser.hourlyRateMin || TIMPRIS_MIN;
  const timprisMax = priser.hourlyRateMax || TIMPRIS_MAX;
  const materialpris = priser.materialHandlingRate || MATERIALPRIS;

  const plan = offert?.paymentPlan || offert?.puData?.paymentPlan;
  const sistaPosten = plan?.installments?.[plan.installments.length - 1];
  const konfigProcent = plan?.type === 'prestationsbunden'
    ? plan?.prestationsbundenConfig?.slutfakturaProcent
    : plan?.varannanVeckaConfig?.slutfakturaProcent;
  const innehallning = sistaPosten?.procent || konfigProcent || INNEHALLNING_STANDARD;

  const villkor = [];

  // För en BRF gäller varken ABS18 eller konsumenttjänstlagen — beställaren är
  // en juridisk person. Vilket regelverk som ska stå här är inte beslutat, så
  // punkten utelämnas hellre än att ange fel lag i ett avtalsdokument.
  if (!arBrf) {
    villkor.push({
      text: arTillbyggnad
        ? 'ABS18 gäller för arbetet.'
        : 'Konsumenttjänstlagen (1985:716) gäller för arbetet.',
      highlight: true,
    });
  }

  villkor.push(
    { text: 'Beställare står för el-, vatten-, och eventuella avstängningskostnader under projekttiden.' },
    { text: 'Beställare ansvarar för att förse entreprenören med skisser, ritningar och underlag samt fatta nödvändiga beslut för att kunna genomföra projektet och undvika förlängning av projekttiden och tillkommande kostnader.' },
    { text: `Beställare ansvarar för inköp av material som inte ingår i offert och att detta tillhandahålls arbetsplatsen på överenskommen tid för att undvika förseningar och tillkommande kostnader. Montering av försenat material debiteras ${timprisMin} till ${timprisMax} SEK inkl. moms/h på löpande räkning plus transportavgifter.` },
    { text: `Entreprenören kan köpa in material som inte ingår i offert åt beställare till inköpspris +10% samt bära upp och/eller hämta material åt beställare för ${materialpris} SEK inkl. moms/h plus transportavgifter.` },
    { text: `Entreprenören ansvarar inte för dammspridning eller skada på lösöre inom arbetsområdet och kan flytta möbler åt beställare vid behov för ${materialpris} SEK inkl. moms/h.` },
    { text: `Entreprenören kan utföra ändringar, tillägg och avgående arbeten (ÄTA) och då faktureras eller krediteras beställare ${timprisMin} till ${timprisMax} SEK/h inkl. moms på löpande räkning plus transportavgifter om inget annat överenskommits.` },
    { text: 'Entreprenören reserverar sig för att ändringar, tillägg och avgående arbeten (ÄTA) kan påverka tidplanen. ÄTA-arbeten faktureras separat efter att de är utförda med betalningsvillkor 10 dagar netto.' },
  );

  // Vitet står direkt efter ÄTA-punkten om tidplanen — den punkten är det som
  // flyttar färdigställandedagen som vitet räknas från.
  // En egen text (t.ex. en jurist-kund som vill ha sin formulering) ersätter
  // standardtexten helt — men bara så länge vitet är ikryssat.
  const vite = byggVitestext(offert, { arBrf });
  if (vite) villkor.push({ text: vite, highlight: true });

  villkor.push(
    { text: 'Projektet faktureras enligt betalplan.' },
    { text: 'Entreprenören har rätt att slutfakturera när arbetet är färdigställt med betalningsvillkor 10 dagar netto.' },
    { text: `Beställare har rätt att hålla ${innehallning}% av totalbeloppet tills eventuella anmärkningar är avhjälpta. Detta inkluderar ej försenat material beställt av beställare.` },
    { text: 'Entreprenören åtgärdar och bekostar eventuella anmärkningar. Tid för att avhjälpa eventuella anmärkningar tillkommer utöver den angivna projekttiden.' },
  );

  // En förening har ingen rätt till ROT-avdrag, så punkten om utebliven
  // utbetalning saknar mening för dem.
  if (!arBrf) {
    villkor.push({ text: 'Beställare ansvarar för att betala totalbeloppet i offert vid utebliven ROT-utbetalning från Skatteverket.' });
  }

  villkor.push({
    text: harHembesok
      ? 'Entreprenören har lämnat offert baserad på en okulär bedömning och tar ej hänsyn till dolda fel eller oförutsedda komplikationer samt eventuella kostnader och tidstillägg för att åtgärda dessa.'
      : 'Platsbesök har ej genomförts. Offerten baseras på information från beställare samt antaganden och uppskattningar som gjorts av entreprenören. Arbetsbeskrivningen, tidsåtgången och priset är indikativt och kan komma att justeras.',
  });

  villkor.push({ text: byggGiltighetstext(offert), highlight: true });

  return villkor;
};

/**
 * Vite vid försening, eller null när inget vite är avtalat.
 *
 * Formuleringen följer det som är brukligt i ABS 18 och AB 04:
 *  - per PÅBÖRJAD vecka, så en försening på tre dagar ger en veckas vite
 *  - räknas från den avtalade färdigställandedagen till dess arbetet är klart
 *  - bara förseningar entreprenören svarar för; ÄTA, beställarens hinder och
 *    försenat material från beställaren flyttar dagen (samma undantag som
 *    punkterna om material och ÄTA ovan redan anger)
 *  - sista meningen skiljer sig mellan konsument och förening, se nedan
 *
 * Konsumenttjänstlagen är tvingande till konsumentens förmån (3 §): ett villkor
 * som ger konsumenten sämre rätt än lagen gäller inte mot hen. Lagen ger rätt
 * till skadestånd för hela den skada en försening orsakar (31 §). En mening om
 * att vitet ÄR ersättningen för förseningen hade alltså begränsat den rätten
 * — därför står det för konsument uttryckligen att rätten finns kvar. För en
 * förening (AB 04-läget) gäller inte lagen, och där är det brukligt att vitet
 * är den enda ersättningen för förseningen.
 *
 * Datumet skrivs bara ut när det är ett riktigt datum. Fritext som "v.32"
 * eller ett saknat datum ger i stället "den avtalade färdigställandedagen
 * enligt tidplanen".
 *
 * Exporteras för att kalkylens viteseditor ska kunna förifylla en egen text
 * med standardlydelsen. `vite.egenText` ersätter standardtexten när den finns.
 */
export const byggVitestext = (offert, { arBrf = false, ignoreraEgenText = false } = {}) => {
  const v = offert?.vite;
  const belopp = Math.round(Number(v?.beloppPerVecka) || 0);
  if (!v?.aktivt || belopp <= 0) return null;

  const egen = String(v.egenText || '').trim();
  if (egen && !ignoreraEgenText) return egen;

  const slut = offert?.projectEndDate;
  const arDatum = slut && /^\d{4}-\d{2}-\d{2}/.test(String(slut));
  const fran = arDatum
    ? `den avtalade färdigställandedagen ${formateraVillkorsdatum(slut)}`
    : 'den avtalade färdigställandedagen enligt tidplanen';

  // Taket skrivs även i kronor, räknat på kontraktssumman inkl. moms (före
  // ROT) — samma belopp som avtalens kontraktssumma för konsument.
  // Samma källa och fallback som PricingSection: nytt format först, puData för äldre.
  const kundoffert = offert?.customerPricing?.kundoffert || offert?.puData?.customerPricing?.kundoffert;
  const kontraktssumma = Number(kundoffert?.inklMoms?.total) || 0;
  const takProcent = Number(v.takProcent) || 0;
  const takBelopp = Math.round((kontraktssumma * takProcent) / 100);
  const tak = takProcent > 0
    ? ` Vitet uppgår sammanlagt till högst ${String(takProcent).replace('.', ',')} % av kontraktssumman inkl. moms${
      takBelopp > 0 ? `, motsvarande ${takBelopp.toLocaleString('sv-SE')} SEK inkl. moms` : ''}.`
    : '';

  return `Vite vid försening: Blir arbetet inte färdigställt i tid av skäl som entreprenören svarar för, har beställare rätt till vite med ${belopp.toLocaleString('sv-SE')} SEK inkl. moms per påbörjad vecka. Vitet räknas från ${fran} till dess att arbetet är färdigställt.${tak} Förlängs tiden på grund av ÄTA-arbeten, hinder från beställare, försenat material som beställare ansvarar för eller omständigheter som entreprenören inte råder över, flyttas färdigställandedagen i motsvarande mån. ${arBrf
    ? 'Vitet utgör beställarens ersättning för förseningen.'
    : 'Vitet begränsar inte beställarens rätt enligt konsumenttjänstlagen att häva avtalet eller få ersättning för skada som överstiger vitet.'}`;
};

/** Giltighetstexten, som skiljer sig beroende på om offerten hunnit skickas. */
const byggGiltighetstext = (offert) => {
  if (offert?.expiresAt && offert?.sentAt) {
    const dagar = Math.ceil((new Date(offert.expiresAt) - new Date(offert.sentAt)) / 86400000);
    return `Offerten är giltig till ${formateraVillkorsdatum(offert.expiresAt)} (${dagar} dagar).`;
  }
  if (offert?.expiresAt) {
    return `Offerten är giltig till ${formateraVillkorsdatum(offert.expiresAt)}.`;
  }
  const dagar = offert?.validityDays || GILTIGHET_DAGAR;
  const framtida = new Date(Date.now() + dagar * 86400000);
  return `Offerten är giltig till ${formateraVillkorsdatum(framtida.toISOString())} (${dagar} dagar).`;
};
